import { API_BASE_URL } from '../../config/api';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Counter from 'yet-another-react-lightbox/plugins/counter';

import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import { useTranslation } from 'react-i18next';
import SEO from '../component/SEO';

const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const ReportPhoto = () => {
    const { t, i18n } = useTranslation();
    const [galleries, setGalleries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Количество подгруженных месяцев в бесконечной ленте
    const [visibleMonthCount, setVisibleMonthCount] = useState(2);
    // Активный месяц на экране (ScrollSpy)
    const [activeSectionKey, setActiveSectionKey] = useState('');
    // Выбранный год для мобильных чипов
    const [mobileSelectedYear, setMobileSelectedYear] = useState('');
    // Раскрытые года в левом аккордеоне (ПК)
    const [expandedYears, setExpandedYears] = useState({});
    // Плавающая кнопка "Наверх"
    const [showBackToTop, setShowBackToTop] = useState(false);

    // Lightbox
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const sentinelRef = useRef(null);
    const isAutoScrollingRef = useRef(false);

    // 1. Загрузка всех данных галереи
    useEffect(() => {
        setIsLoading(true);
        axios.get(`${API_BASE_URL}/raport/gallery/`, {
            headers: {
                'Accept-Language': i18n.language
            },
        })
        .then(response => {
            const data = response.data || [];
            const validGalleries = data.filter(g => g.gallery_shots && g.gallery_shots.length > 0);
            setGalleries(validGalleries);
            setIsLoading(false);
        })
        .catch(error => {
            console.error('Error fetching gallery:', error);
            setIsLoading(false);
        });
    }, [i18n.language]);

    // 2. Группировка всех месяцев от новых к старым
    const allMonthSections = useMemo(() => {
        if (!galleries.length) return [];
        const sectionMap = new Map();

        galleries.forEach(g => {
            const yr = g.year?.name || g.year?.name_ru || g.year?.name_ro;
            if (!yr || !g.name) return;
            const monthId = g.name.id;
            let monthName = g.name.name;
            if (i18n.language === 'ro' && g.name.name_ro) monthName = g.name.name_ro;
            else if (i18n.language === 'ru' && g.name.name_ru) monthName = g.name.name_ru;
            else if (i18n.language === 'en' && g.name.name_en) monthName = g.name.name_en;

            const key = `${yr}-${monthId}`;
            const shots = (g.gallery_shots || []).map(s => ({
                ...s,
                image_url: getFullImageUrl(s.image_url || s.image || '')
            }));

            if (sectionMap.has(key)) {
                const existing = sectionMap.get(key);
                existing.shots = [...existing.shots, ...shots];
                existing.count = existing.shots.length;
            } else {
                sectionMap.set(key, {
                    key,
                    year: yr,
                    monthId,
                    monthName,
                    count: shots.length,
                    shots
                });
            }
        });

        // Сортировка: года от новых к старым (2025, 2024...), месяцы от 12 к 1
        return Array.from(sectionMap.values()).sort((a, b) => {
            if (Number(b.year) !== Number(a.year)) {
                return Number(b.year) - Number(a.year);
            }
            return Number(b.monthId) - Number(a.monthId);
        });
    }, [galleries, i18n.language]);

    // 3. Дерево годов и месяцев для бокового меню и мобильных чипов
    const sidebarTree = useMemo(() => {
        const yearsMap = new Map();

        allMonthSections.forEach(sec => {
            if (!yearsMap.has(sec.year)) {
                yearsMap.set(sec.year, {
                    year: sec.year,
                    totalPhotos: 0,
                    months: []
                });
            }
            const yrObj = yearsMap.get(sec.year);
            yrObj.totalPhotos += sec.count;
            yrObj.months.push({
                key: sec.key,
                id: sec.monthId,
                name: sec.monthName,
                count: sec.count
            });
        });

        return Array.from(yearsMap.values()).sort((a, b) => Number(b.year) - Number(a.year));
    }, [allMonthSections]);

    // Общее количество фото
    const totalAllPhotos = useMemo(() => {
        return allMonthSections.reduce((acc, s) => acc + s.count, 0);
    }, [allMonthSections]);

    // Инициализация первого активного периода
    useEffect(() => {
        if (allMonthSections.length > 0 && !activeSectionKey) {
            const first = allMonthSections[0];
            setActiveSectionKey(first.key);
            setMobileSelectedYear(first.year);
            setExpandedYears({ [first.year]: true });
        }
    }, [allMonthSections, activeSectionKey]);

    // 4. Infinite Scroll (Автоматическая подгрузка при скролле вниз)
    useEffect(() => {
        if (isLoading || visibleMonthCount >= allMonthSections.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleMonthCount(prev => Math.min(prev + 1, allMonthSections.length));
                }
            },
            { rootMargin: '400px 0px', threshold: 0.05 }
        );

        const currentSentinel = sentinelRef.current;
        if (currentSentinel) {
            observer.observe(currentSentinel);
        }

        return () => {
            if (currentSentinel) {
                observer.unobserve(currentSentinel);
            }
        };
    }, [isLoading, visibleMonthCount, allMonthSections.length]);

    // 5. ScrollSpy (Отслеживание текущего месяца на экране)
    useEffect(() => {
        const handleScroll = () => {
            setShowBackToTop(window.scrollY > 400);

            if (isAutoScrollingRef.current) return;

            const sections = document.querySelectorAll('.month-feed-section');
            const scrollPos = window.scrollY + 140;

            for (let i = sections.length - 1; i >= 0; i--) {
                const sec = sections[i];
                if (sec.offsetTop <= scrollPos) {
                    const key = sec.getAttribute('data-key');
                    const year = sec.getAttribute('data-year');
                    if (key && key !== activeSectionKey) {
                        setActiveSectionKey(key);
                        if (year) {
                            setMobileSelectedYear(year);
                            setExpandedYears(prev => ({ ...prev, [year]: true }));
                        }
                    }
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeSectionKey]);

    // 6. Клик по месяцу — мгновенный переход
    const handleJumpToMonth = useCallback((targetKey, year) => {
        const targetIndex = allMonthSections.findIndex(s => s.key === targetKey);
        if (targetIndex === -1) return;

        if (targetIndex >= visibleMonthCount) {
            setVisibleMonthCount(targetIndex + 1);
        }

        setActiveSectionKey(targetKey);
        if (year) {
            setMobileSelectedYear(year);
            setExpandedYears(prev => ({ ...prev, [year]: true }));
        }
        isAutoScrollingRef.current = true;

        setTimeout(() => {
            const el = document.getElementById(`section-${targetKey}`);
            if (el) {
                const isMobile = window.innerWidth <= 860;
                const headerOffset = isMobile ? 120 : 80;
                const elementPosition = el.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
            setTimeout(() => {
                isAutoScrollingRef.current = false;
            }, 750);
        }, 80);
    }, [allMonthSections, visibleMonthCount]);

    const toggleYearExpand = (year) => {
        setExpandedYears(prev => ({
            ...prev,
            [year]: !prev[year]
        }));
    };

    // Месяцы для выбранного мобильного года
    const activeMobileMonths = useMemo(() => {
        const currentYear = mobileSelectedYear || (sidebarTree[0]?.year || '');
        const found = sidebarTree.find(y => y.year === currentYear);
        return found ? found.months : [];
    }, [sidebarTree, mobileSelectedYear]);

    const visibleSections = useMemo(() => {
        return allMonthSections.slice(0, visibleMonthCount);
    }, [allMonthSections, visibleMonthCount]);

    // Список всех загруженных слайдов для Lightbox
    const allVisibleSlides = useMemo(() => {
        const slides = [];
        visibleSections.forEach(sec => {
            sec.shots.forEach(img => {
                slides.push({
                    src: img.image_url,
                    alt: img.title && img.title !== 'null' ? img.title : `${sec.monthName} ${sec.year}`,
                });
            });
        });
        return slides;
    }, [visibleSections]);

    const openLightboxAt = (secIndex, imgIndex) => {
        let globalIndex = 0;
        for (let i = 0; i < secIndex; i++) {
            globalIndex += visibleSections[i].shots.length;
        }
        globalIndex += imgIndex;
        setLightboxIndex(globalIndex);
        setLightboxOpen(true);
    };

    const uiTexts = useMemo(() => {
        if (i18n.language === 'ro') {
            return {
                title: 'Rapoarte foto',
                description: 'Aici spunem și arătăm cum distribuim produse',
                archiveTitle: 'Cronologia rapoartelor',
                allPhotos: 'fotografii',
                loadingMore: 'Se încarcă rapoartele următoare...',
                endOfFeed: 'Ați vizualizat toate rapoartele foto din 2021 până în 2025',
                backToTop: 'Sus',
                photosSuffix: 'fotografii',
            };
        } else if (i18n.language === 'ru') {
            return {
                title: 'Фотоотчеты',
                description: 'Здесь мы рассказываем и показываем, как распределяем продукты',
                archiveTitle: 'Хроника отчётов',
                allPhotos: 'фото',
                loadingMore: 'Автоматическая подгрузка следующих отчётов...',
                endOfFeed: 'Вы просмотрели весь архив фотоотчётов (2021 — 2025)',
                backToTop: 'Наверх',
                photosSuffix: 'фото',
            };
        }
        return {
            title: 'Photo Reports',
            description: 'Here we show and tell how we distribute products',
            archiveTitle: 'Reports Timeline',
            allPhotos: 'photos',
            loadingMore: 'Loading next reports...',
            endOfFeed: 'You have viewed all photo reports (2021 — 2025)',
            backToTop: 'Top',
            photosSuffix: 'photos',
        };
    }, [i18n.language]);

    return (
        <div className='test_block' style={{ minHeight: '650px', padding: '18px 0 80px' }}>
            <SEO />
            <style>{`
                .photo-reports-layout {
                    display: flex;
                    gap: 28px;
                    align-items: flex-start;
                    position: relative;
                }
                .photo-reports-sidebar {
                    width: 250px;
                    flex-shrink: 0;
                    position: sticky;
                    top: 85px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
                    padding: 16px 12px;
                    max-height: calc(100vh - 110px);
                    overflow-y: auto;
                }
                .photo-reports-sidebar::-webkit-scrollbar {
                    width: 4px;
                }
                .photo-reports-sidebar::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 4px;
                }
                .photo-reports-content {
                    flex: 1;
                    min-width: 0;
                }
                .photo-masonry-gallery {
                    column-count: 3;
                    column-gap: 16px;
                }
                
                /* ДЕСКТОП: Скрываем мобильную панель */
                @media (min-width: 861px) {
                    .mobile-timeline-container {
                        display: none !important;
                    }
                }

                /* ПЛАНШЕТЫ */
                @media (max-width: 1200px) and (min-width: 861px) {
                    .photo-masonry-gallery {
                        column-count: 2;
                        column-gap: 14px;
                    }
                }

                /* МОБИЛЬНЫЕ УСТРОЙСТВА И ANDROID */
                @media (max-width: 860px) {
                    .photo-reports-layout {
                        display: block;
                    }
                    /* Скрываем громоздкую вертикальную боковую колонку на смартфонах */
                    .photo-reports-sidebar {
                        display: none !important;
                    }
                    /* Компактная плавающая горизонтальная панель для свайпа пальцем */
                    .mobile-timeline-container {
                        display: block !important;
                        position: sticky;
                        top: 55px;
                        z-index: 40;
                        background: rgba(255, 255, 255, 0.96);
                        backdrop-filter: blur(12px);
                        -webkit-backdrop-filter: blur(12px);
                        padding: 8px 4px 10px;
                        margin-bottom: 16px;
                        border-bottom: 1px solid #e2e8f0;
                        border-radius: 0 0 14px 14px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.03);
                    }
                    .mobile-scroll-row {
                        display: flex;
                        overflow-x: auto;
                        gap: 6px;
                        padding: 2px 4px;
                        scrollbar-width: none;
                        -ms-overflow-style: none;
                        -webkit-overflow-scrolling: touch;
                    }
                    .mobile-scroll-row::-webkit-scrollbar {
                        display: none;
                    }
                    .mobile-year-btn {
                        padding: 5px 14px;
                        border-radius: 20px;
                        font-size: 13px;
                        font-weight: 700;
                        border: 1px solid #e2e8f0;
                        background: #ffffff;
                        color: #475569;
                        white-space: nowrap;
                        cursor: pointer;
                        flex-shrink: 0;
                        transition: all 0.2s ease;
                        outline: none;
                    }
                    .mobile-year-btn.active {
                        background: #0DB97A;
                        border-color: #0DB97A;
                        color: #ffffff;
                        box-shadow: 0 2px 8px rgba(13, 185, 122, 0.28);
                    }
                    .mobile-month-btn {
                        padding: 5px 11px;
                        border-radius: 8px;
                        font-size: 12px;
                        font-weight: 600;
                        border: 1px solid #e2e8f0;
                        background: #f8fafc;
                        color: #334155;
                        white-space: nowrap;
                        cursor: pointer;
                        flex-shrink: 0;
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        transition: all 0.2s ease;
                        outline: none;
                    }
                    .mobile-month-btn.active {
                        background: #ecfdf5;
                        border-color: #0DB97A;
                        color: #047857;
                        font-weight: 700;
                    }
                    .mobile-month-btn .month-badge {
                        font-size: 10px;
                        padding: 1px 5px;
                        border-radius: 6px;
                        background: #e2e8f0;
                        color: #64748b;
                        font-weight: 700;
                    }
                    .mobile-month-btn.active .month-badge {
                        background: #0DB97A;
                        color: #ffffff;
                    }
                    .photo-masonry-gallery {
                        column-count: 2;
                        column-gap: 10px;
                    }
                    .masonry-photo-item {
                        margin-bottom: 10px !important;
                        border-radius: 10px !important;
                    }
                    .month-header-card {
                        padding: 10px 14px !important;
                        margin-bottom: 12px !important;
                        border-radius: 10px !important;
                    }
                    .month-header-card h4 {
                        font-size: 15px !important;
                    }
                }

                @media (max-width: 420px) {
                    .photo-masonry-gallery {
                        column-count: 2;
                        column-gap: 8px;
                    }
                    .masonry-photo-item {
                        margin-bottom: 8px !important;
                    }
                }

                .masonry-photo-item {
                    break-inside: avoid;
                    margin-bottom: 16px;
                    position: relative;
                    border-radius: 14px;
                    overflow: hidden;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 3px 12px rgba(0,0,0,0.04);
                    cursor: pointer;
                    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.28s ease, border-color 0.28s ease;
                    display: block;
                }
                .masonry-photo-item:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px rgba(13, 185, 122, 0.16);
                    border-color: #0DB97A;
                }
                .masonry-photo-item img {
                    width: 100%;
                    height: auto;
                    display: block;
                    transition: transform 0.35s ease;
                }
                .masonry-photo-item:hover img {
                    transform: scale(1.025);
                }
                .masonry-photo-item .photo-expand-btn {
                    position: absolute;
                    bottom: 10px;
                    right: 10px;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(6px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 3px 8px rgba(0,0,0,0.18);
                    opacity: 0;
                    transform: translateY(4px);
                    transition: opacity 0.25s ease, transform 0.25s ease;
                    color: #0f172a;
                }
                .masonry-photo-item:hover .photo-expand-btn {
                    opacity: 1;
                    transform: translateY(0);
                }
            `}</style>

            <div className="container">
                {/* Заголовок страницы */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h3 className="main-content__main-products desktop-title tac" style={{ marginBottom: '6px' }}>
                        {uiTexts.title}
                    </h3>
                    <div className="desktop-content tac">
                        <p style={{ color: '#64748b', fontSize: '14.5px', maxWidth: '640px', margin: '0 auto' }}>
                            {uiTexts.description}
                        </p>
                    </div>
                </div>

                {/* МОБИЛЬНАЯ / ANDROID НАВИГАЦИЯ (Компактный горизонтальный свайп по годам и месяцам) */}
                <div className="mobile-timeline-container">
                    {/* 1. Года (свайп пальцем) */}
                    <div className="mobile-scroll-row">
                        {sidebarTree.map(y => {
                            const isYearSelected = y.year === (mobileSelectedYear || sidebarTree[0]?.year);
                            return (
                                <button
                                    key={y.year}
                                    onClick={() => {
                                        setMobileSelectedYear(y.year);
                                        if (y.months.length > 0) {
                                            handleJumpToMonth(y.months[0].key, y.year);
                                        }
                                    }}
                                    className={`mobile-year-btn ${isYearSelected ? 'active' : ''}`}
                                >
                                    {y.year}
                                </button>
                            );
                        })}
                    </div>

                    {/* 2. Месяцы выбранного года (свайп пальцем) */}
                    {activeMobileMonths.length > 0 && (
                        <div className="mobile-scroll-row" style={{ marginTop: '6px' }}>
                            {activeMobileMonths.map(m => {
                                const isMonthActive = m.key === activeSectionKey;
                                return (
                                    <button
                                        key={m.key}
                                        onClick={() => handleJumpToMonth(m.key, mobileSelectedYear)}
                                        className={`mobile-month-btn ${isMonthActive ? 'active' : ''}`}
                                    >
                                        <span>{m.name}</span>
                                        <span className="month-badge">{m.count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ОСНОВНОЙ МАКЕТ С ЗАКРЕПЛЕННЫМ ТАЙМЛАЙНОМ И БЕСКОНЕЧНОЙ ЛЕНТОЙ */}
                <div className="photo-reports-layout">
                    {/* 1. ЛЕВАЯ КОЛОНКА: СТИЛЬНЫЙ ЛИПКИЙ ТАЙМЛАЙН СО SCROLLSPY (Только для ПК) */}
                    <aside className="photo-reports-sidebar">
                        {/* Заголовок таймлайна */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingBottom: '12px',
                            marginBottom: '12px',
                            borderBottom: '1px solid #f1f5f9'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#0DB97A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                <span style={{ fontSize: '13.5px', fontWeight: '700', color: '#0f172a' }}>
                                    {uiTexts.archiveTitle}
                                </span>
                            </div>
                            <span style={{
                                fontSize: '11px',
                                fontWeight: '700',
                                color: '#64748b',
                                background: '#f1f5f9',
                                padding: '2px 7px',
                                borderRadius: '10px'
                            }}>
                                {totalAllPhotos} {uiTexts.allPhotos}
                            </span>
                        </div>

                        {/* Интерактивное дерево-таймлайн */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {sidebarTree.map(y => {
                                const isExpanded = !!expandedYears[y.year];
                                const hasActiveMonthInYear = y.months.some(m => m.key === activeSectionKey);

                                return (
                                    <div key={y.year} style={{ borderRadius: '8px', overflow: 'hidden' }}>
                                        {/* Кнопка года */}
                                        <div
                                            onClick={() => toggleYearExpand(y.year)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '7px 10px',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                background: hasActiveMonthInYear ? '#f0fdf4' : 'transparent',
                                                border: hasActiveMonthInYear ? '1px solid #bbf7d0' : '1px solid transparent',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!hasActiveMonthInYear) e.currentTarget.style.background = '#f8fafc';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!hasActiveMonthInYear) e.currentTarget.style.background = 'transparent';
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                                <svg
                                                    width="11"
                                                    height="11"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke={hasActiveMonthInYear ? '#059669' : '#94a3b8'}
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    style={{
                                                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s ease'
                                                    }}
                                                >
                                                    <polyline points="9 18 15 12 9 6" />
                                                </svg>
                                                <span style={{
                                                    fontSize: '13.5px',
                                                    fontWeight: hasActiveMonthInYear ? '700' : '600',
                                                    color: hasActiveMonthInYear ? '#047857' : '#334155'
                                                }}>
                                                    {y.year}
                                                </span>
                                            </div>
                                            <span style={{
                                                fontSize: '10.5px',
                                                fontWeight: '600',
                                                color: hasActiveMonthInYear ? '#047857' : '#94a3b8',
                                                background: hasActiveMonthInYear ? '#dcfce7' : '#f8fafc',
                                                padding: '1px 6px',
                                                borderRadius: '8px'
                                            }}>
                                                {y.totalPhotos}
                                            </span>
                                        </div>

                                        {/* Месяцы года */}
                                        {isExpanded && (
                                            <div style={{
                                                marginLeft: '15px',
                                                paddingLeft: '10px',
                                                borderLeft: '2px solid #e2e8f0',
                                                marginTop: '3px',
                                                marginBottom: '5px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '2px'
                                            }}>
                                                {y.months.map(m => {
                                                    const isMonthActive = m.key === activeSectionKey;
                                                    return (
                                                        <div
                                                            key={m.key}
                                                            onClick={() => handleJumpToMonth(m.key, y.year)}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                padding: '5px 8px',
                                                                borderRadius: '6px',
                                                                cursor: 'pointer',
                                                                background: isMonthActive ? '#0DB97A' : 'transparent',
                                                                color: isMonthActive ? '#ffffff' : '#475569',
                                                                fontWeight: isMonthActive ? '700' : '500',
                                                                fontSize: '12.5px',
                                                                position: 'relative',
                                                                transition: 'all 0.15s ease'
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (!isMonthActive) {
                                                                    e.currentTarget.style.background = '#f1f5f9';
                                                                    e.currentTarget.style.color = '#0f172a';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (!isMonthActive) {
                                                                    e.currentTarget.style.background = 'transparent';
                                                                    e.currentTarget.style.color = '#475569';
                                                                }
                                                            }}
                                                        >
                                                            {/* Активная точка-индикатор */}
                                                            {isMonthActive && (
                                                                <span style={{
                                                                    position: 'absolute',
                                                                    left: '-16px',
                                                                    top: '50%',
                                                                    transform: 'translateY(-50%)',
                                                                    width: '10px',
                                                                    height: '10px',
                                                                    borderRadius: '50%',
                                                                    background: '#0DB97A',
                                                                    border: '2px solid #ffffff',
                                                                    boxShadow: '0 0 0 2px #0DB97A'
                                                                }} />
                                                            )}
                                                            <span>{m.name}</span>
                                                            <span style={{
                                                                fontSize: '10.5px',
                                                                fontWeight: '600',
                                                                opacity: isMonthActive ? 0.95 : 0.75,
                                                                background: isMonthActive ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
                                                                color: isMonthActive ? '#ffffff' : '#64748b',
                                                                padding: '1px 5px',
                                                                borderRadius: '6px'
                                                            }}>
                                                                {m.count}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </aside>

                    {/* 2. ПРАВАЯ КОЛОНКА: БЕСКОНЕЧНАЯ ЛЕНТА (INFINITE FEED) */}
                    <main className="photo-reports-content">
                        {isLoading ? (
                            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                                <div style={{
                                    display: 'inline-block',
                                    width: '36px',
                                    height: '36px',
                                    border: '3px solid #e2e8f0',
                                    borderTopColor: '#0DB97A',
                                    borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite'
                                }} />
                                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                                <p style={{ fontSize: '14.5px', color: '#64748b', marginTop: '12px' }}>
                                    {t('common.loading') || 'Загрузка...'}
                                </p>
                            </div>
                        ) : visibleSections.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '60px 0', background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                <p style={{ fontSize: '15px', color: '#94a3b8' }}>{t('reportPhoto.noImages') || 'Нет фотографий'}</p>
                            </div>
                        ) : (
                            <>
                                {/* Секции месяцев в бесконечной ленте */}
                                {visibleSections.map((sec, secIdx) => (
                                    <section
                                        key={sec.key}
                                        id={`section-${sec.key}`}
                                        className="month-feed-section"
                                        data-key={sec.key}
                                        data-year={sec.year}
                                        style={{ marginBottom: '35px', scrollMarginTop: '110px' }}
                                    >
                                        {/* Заголовок месяца */}
                                        <div className="month-header-card" style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '14px',
                                            padding: '12px 18px',
                                            background: '#ffffff',
                                            borderRadius: '14px',
                                            border: '1px solid #e2e8f0',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '8px',
                                                    background: '#ecfdf5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#0DB97A'
                                                }}>
                                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="3" y="3" width="18" height="18" rx="4" ry="4" />
                                                        <circle cx="8.5" cy="8.5" r="1.5" />
                                                        <polyline points="21 15 16 10 5 21" />
                                                    </svg>
                                                </div>
                                                <h4 style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
                                                    {sec.monthName} {sec.year}
                                                </h4>
                                            </div>
                                            <span style={{
                                                fontSize: '12px',
                                                color: '#047857',
                                                background: '#ecfdf5',
                                                border: '1px solid #a7f3d0',
                                                padding: '3px 10px',
                                                borderRadius: '20px',
                                                fontWeight: '600'
                                            }}>
                                                {sec.count} {uiTexts.photosSuffix}
                                            </span>
                                        </div>

                                        {/* Masonry галерея */}
                                        <div className="photo-masonry-gallery">
                                            {sec.shots.map((image, imgIdx) => (
                                                <div
                                                    key={image.id || imgIdx}
                                                    className="masonry-photo-item"
                                                    onClick={() => openLightboxAt(secIdx, imgIdx)}
                                                >
                                                    <img
                                                        src={image.image_url}
                                                        alt={image.title && image.title !== 'null' ? image.title : `${sec.monthName} ${sec.year}`}
                                                        loading="lazy"
                                                    />
                                                    <div className="photo-expand-btn">
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="15 3 21 3 21 9" />
                                                            <polyline points="9 21 3 21 3 15" />
                                                            <line x1="21" y1="3" x2="14" y2="10" />
                                                            <line x1="3" y1="21" x2="10" y2="14" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                ))}

                                {/* Сентинел для автоматической подгрузки */}
                                <div ref={sentinelRef} style={{ height: '30px', margin: '15px 0' }} />

                                {/* Индикатор загрузки / конец ленты */}
                                {visibleMonthCount < allMonthSections.length ? (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '20px 0',
                                        color: '#64748b',
                                        fontSize: '13.5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px'
                                    }}>
                                        <div style={{
                                            width: '18px',
                                            height: '18px',
                                            border: '2px solid #cbd5e1',
                                            borderTopColor: '#0DB97A',
                                            borderRadius: '50%',
                                            animation: 'spin 0.8s linear infinite'
                                        }} />
                                        <span>{uiTexts.loadingMore}</span>
                                    </div>
                                ) : (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '24px 16px',
                                        background: '#ffffff',
                                        borderRadius: '14px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                                        color: '#059669',
                                        fontWeight: '600',
                                        fontSize: '13.5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}>
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        <span>{uiTexts.endOfFeed}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </main>
                </div>

                {/* ПЛАВАЮЩАЯ КНОПКА "НАВЕРХ" */}
                {showBackToTop && (
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        title={uiTexts.backToTop}
                        style={{
                            position: 'fixed',
                            bottom: '24px',
                            right: '24px',
                            width: '44px',
                            height: '44px',
                            borderRadius: '50%',
                            background: '#0DB97A',
                            color: '#ffffff',
                            border: 'none',
                            boxShadow: '0 6px 18px rgba(13, 185, 122, 0.35)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 99,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="18 15 12 9 6 15" />
                        </svg>
                    </button>
                )}

                {/* 4. Lightbox Просмотрщик */}
                <Lightbox
                    open={lightboxOpen}
                    close={() => setLightboxOpen(false)}
                    slides={allVisibleSlides}
                    index={lightboxIndex}
                    plugins={[Thumbnails, Zoom, Fullscreen, Counter]}
                    thumbnails={{
                        position: "bottom",
                        width: 75,
                        height: 55,
                        gap: 8,
                        borderRadius: 6
                    }}
                    zoom={{
                        maxZoomPixelRatio: 3,
                        zoomInMultiplier: 2
                    }}
                />
            </div>
        </div>
    );
};

export default ReportPhoto;
